import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';

interface Game {
  id: string;
  opponentName: string;
  opponentEmail: string | null;
  leagueName: string | null;
  legs: number;
  scheduledStart: string;
  scheduledEnd: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  notes: string | null;
  createdBy: 'owner' | 'opponent';
  ownerLegsWon: number | null;
  opponentLegsWon: number | null;
  winner: string | null;
}

const STATUS_COLORS = {
  scheduled: 'bg-blue-900/30 text-blue-300 border-blue-800/50',
  completed: 'bg-green-900/30 text-green-300 border-green-800/50',
  cancelled: 'bg-gray-800/30 text-gray-400 border-gray-700/50',
  no_show: 'bg-red-900/30 text-red-300 border-red-800/50'
};

export default function Games() {
  const { user } = useAuth();
  const [games, setGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<string>('');

  // Complete game modal
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [ownerScore, setOwnerScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchGames = async () => {
    try {
      const params: { status?: string } = {};
      if (filter) params.status = filter;

      const response = await api.get('/games', { params });
      setGames(response.data.games);
    } catch {
      setError('Failed to load games');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
  }, [filter]);

  const handleCancel = async (gameId: string) => {
    if (!confirm('Cancel this game?')) return;

    try {
      await api.post(`/games/${gameId}/cancel`);
      await fetchGames();
    } catch {
      setError('Failed to cancel game');
    }
  };

  const handleDelete = async (gameId: string) => {
    if (!confirm('Delete this game? This cannot be undone.')) return;

    try {
      await api.delete(`/games/${gameId}`);
      await fetchGames();
    } catch {
      setError('Failed to delete game');
    }
  };

  const openCompleteModal = (game: Game) => {
    setSelectedGame(game);
    setOwnerScore(0);
    setOpponentScore(0);
    setShowCompleteModal(true);
  };

  const handleComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGame) return;

    setIsSubmitting(true);
    try {
      await api.post(`/games/${selectedGame.id}/complete`, {
        ownerLegsWon: ownerScore,
        opponentLegsWon: opponentScore
      });
      await fetchGames();
      setShowCompleteModal(false);
    } catch {
      setError('Failed to complete game');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="page-title mb-0">Games</h1>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="">All Games</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="no_show">No Show</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg text-sm border border-red-800">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {games.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-400">No games found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {games.map((game) => (
            <div key={game.id} className="card">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg text-white">
                      {user?.displayName || 'You'}
                      <span className="text-gray-400 mx-2">vs</span>
                      {game.opponentName}
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_COLORS[game.status]}`}>
                      {game.status}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm">
                    {game.leagueName || 'Casual'} - {game.legs} legs
                  </p>
                  <p className="text-gray-500 text-sm">
                    {format(parseISO(game.scheduledStart), 'EEEE, MMMM d, yyyy')} at{' '}
                    {format(parseISO(game.scheduledStart), 'h:mm a')}
                  </p>
                  {game.status === 'completed' && game.ownerLegsWon !== null && (
                    <p className="text-sm mt-2 text-gray-300">
                      <span className="font-medium">Score:</span>{' '}
                      {game.ownerLegsWon} - {game.opponentLegsWon}
                      {game.winner && (
                        <span className="ml-2 text-gray-400">
                          ({game.winner === 'owner' ? 'You won' : game.winner === 'opponent' ? `${game.opponentName} won` : 'Draw'})
                        </span>
                      )}
                    </p>
                  )}
                  {game.notes && (
                    <p className="text-sm text-gray-500 mt-1">
                      Notes: {game.notes}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {game.status === 'scheduled' && (
                    <>
                      <button
                        onClick={() => openCompleteModal(game)}
                        className="btn-primary text-sm"
                      >
                        Complete
                      </button>
                      <button
                        onClick={() => handleCancel(game.id)}
                        className="btn-secondary text-sm"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {(game.status === 'completed' || game.status === 'cancelled') && (
                    <button
                      onClick={() => handleDelete(game.id)}
                      className="btn-danger text-sm"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Complete Game Modal */}
      {showCompleteModal && selectedGame && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1a1a3a] border border-[#2a2a4a] rounded-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-white mb-4">
              Complete Game vs {selectedGame.opponentName}
            </h2>
            <form onSubmit={handleComplete} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Your Score</label>
                  <input
                    type="number"
                    min="0"
                    value={ownerScore}
                    onChange={(e) => setOwnerScore(parseInt(e.target.value) || 0)}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">{selectedGame.opponentName}'s Score</label>
                  <input
                    type="number"
                    min="0"
                    value={opponentScore}
                    onChange={(e) => setOpponentScore(parseInt(e.target.value) || 0)}
                    className="input"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowCompleteModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="btn-primary">
                  {isSubmitting ? 'Saving...' : 'Save Score'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
